import { DateTime, Interval } from "luxon";
import { DashboardSettingsFiltersDateInput, DatePresets } from "./__generated__/api";

export const SECOND = 1000;
export const MINUTE = SECOND * 60;
export const HOUR = MINUTE * 60;
export const DAY = HOUR * 24;
export const WEEK = DAY * 7;
export const MONTH_30 = DAY * 30;
export const MONTH_31 = DAY * 31;
export const YEAR = DAY * 365;

export const WEEKDAYS = new Array(7)
    .fill(0)
    .map((__, index) => {
        const dayOffset = new Date().getTimezoneOffset() < 0 ? 3 : 2;
        const date = new Date(`2022-01-${(index + dayOffset).toString().padStart(2, "0")}T00:00:00Z`);
        return date.toLocaleDateString(undefined, { weekday: "long" });
    });

function getFirstDayOffsetInLocale(locale: string = navigator.language) {
    const firstDayOfWeek = (
        (new Intl.Locale(locale) as unknown as Record<string, unknown>).weekInfo as {firstDay: number}
    )?.firstDay ?? 7;
    return firstDayOfWeek === 7 ? 0 : 1;
}

export function getStartOfWeek(day: Date, locale?: string) {
    const weekStartOffset = getFirstDayOffsetInLocale(locale);
    const dayOfWeek = day.getDay();
    const diff = dayOfWeek - weekStartOffset + (dayOfWeek === 0 ? 7 : 0);

    return new Date(day.getFullYear(), day.getMonth(), day.getDate() - diff);
}
export function tryParseDate(date: Date | string | undefined | null): Date | undefined | null {
    return typeof date === "string" ? new Date(date) : date;
}

export function isSameDate(date: Date, other: Date) {
    return date.getFullYear() === other.getFullYear()
        && date.getMonth() === other.getMonth()
        && date.getDate() === other.getDate();
}

export function isSameMonth(date: Date, other: Date) {
    return date.getFullYear() === other.getFullYear()
        && date.getMonth() === other.getMonth();
}

export function isSameWeek(date: Date, other: Date) {
    return isSameDate(getStartOfWeek(date), getStartOfWeek(other));
}

// convertDate - returns user friendly date
export function convertDate(millis: string) : string {
    const date = DateTime.fromMillis(parseFloat(millis));
    const duration = Interval.fromDateTimes(date, DateTime.now());
    if (duration.length("days") > 10) {
        return date.toLocaleString(DateTime.DATETIME_SHORT);
    }
    return date.toRelativeCalendar() as string;
}

// getCurrentYear - return current year
export function getCurrentYear(): number {
    return DateTime.now().year;
}

export function timeToHours(value: string): number {
    const parts = value.split(":");
    return (Number.parseInt(parts[0], 10) * 60) + Number.parseInt(parts[1], 10);
}

function parseInRange(value: string, max = 0, min = 0, maxLength = 0) {
    let numeric = Number.parseInt(maxLength > 0
        ? value.substring(0, maxLength)
        : value, 10);
    if (max) {
        numeric = Math.min(numeric, max);
    }
    if (min) {
        numeric = Math.max(numeric, min);
    }
    return numeric;
}

export function timeToHoursWithValidation(value: string): number {
    const parts = value.split(":");
    return (parseInRange(parts[0], 23, 0, 2) * 60)
        + parseInRange(parts[1], 59, 0, 2);
}

export function timeToMilisecondsWithValidation(value: string): number {
    const parts = value.split(":");
    if (parts.length === 2) {
        return (parseInRange(parts[0]) * HOUR)
        + (parseInRange(parts[1], 59, 0, 2) * MINUTE);
    }
    if (parts.length === 3) {
        return (parseInRange(parts[0]) * DAY)
        + (parseInRange(parts[1], 23, 0, 2) * HOUR)
        + (parseInRange(parts[2], 59, 0, 2) * MINUTE);
    }

    return (parseInRange(parts[0]) * DAY)
        + (parseInRange(parts[1], 23, 0, 2) * HOUR)
        + (parseInRange(parts[2], 59, 0, 2) * MINUTE)
        + (parseInRange(parts[3], 59, 0, 2) * SECOND);
}

/* eslint-disable @typescript-eslint/no-shadow */
enum Granularity {
    MILLISECOND = "ms",
    SECOND = "s",
    MINUTE = "m",
    HOUR = "h",
    DAY = "d",
}
/* eslint-enable @typescript-eslint/no-shadow */

const LAST_TO_NEXT_GRANULARITY = {
    [Granularity.DAY]: Granularity.HOUR,
    [Granularity.HOUR]: Granularity.MINUTE,
    [Granularity.MINUTE]: Granularity.SECOND,
    [Granularity.SECOND]: Granularity.MILLISECOND,
    [Granularity.MILLISECOND]: Granularity.MILLISECOND,
};
export function parseDuration(value: string): number {
    const parts = value.split(/[^a-zA-Z0-9]+/);
    let time = 0;
    let lastGranularity = Granularity.HOUR;
    parts.forEach((part, index) => {
        const numeric = Number.parseInt(part, 10);
        if (Number.isNaN(numeric)) {
            return;
        }
        const dayIndex = part.indexOfWithSet(/[a-zA-Z]/);
        let granularity;
        if (dayIndex > 0) {
            granularity = part.substring(dayIndex);
        } else {
            granularity = parts.length > index + 1 ? parts[index + 1] : undefined;
        }

        if (!granularity) {
            granularity = LAST_TO_NEXT_GRANULARITY[lastGranularity];
        }

        if (granularity === "ms" || granularity === "millisecond" || granularity === "milli") {
            time += numeric;
            lastGranularity = Granularity.MILLISECOND;
        } else if (granularity.startsWith("s")) {
            time += numeric * SECOND;
            lastGranularity = Granularity.SECOND;
        } else if (granularity.startsWith("m")) {
            time += numeric * MINUTE;
            lastGranularity = Granularity.MINUTE;
        } else if (granularity.startsWith("h")) {
            time += numeric * HOUR;
            lastGranularity = Granularity.HOUR;
        } else if (granularity.startsWith("d")) {
            time += numeric * DAY;
            lastGranularity = Granularity.DAY;
        }
    });
    return time;
}

export function hoursToTime(value?: number): string {
    if (value === 0) {
        return "00:00";
    }
    if (!value) {
        return "";
    }
    const hour = Math.floor(value / 60).toString().padStart(2, "0");
    const minutes = (value % 60).toString().padStart(2, "0");
    return `${hour}:${minutes}`;
}

export function toUTCInputDate(date: Date): string {
    return [
        date.getUTCFullYear(),
        (date.getUTCMonth() + 1).toString().padStart(2, "0"),
        date.getUTCDate().toString().padStart(2, "0"),
    ].join("-");
}

export function toInputDate(date: Date): string {
    return [
        date.getFullYear(),
        (date.getMonth() + 1).toString().padStart(2, "0"),
        date.getDate().toString().padStart(2, "0"),
    ].join("-");
}

export function toInputHourMinute(date: Date): string {
    return [
        date.getHours().toString().padStart(2, "0"),
        date.getMinutes().toString().padStart(2, "0"),
    ].join(":");
}

export function toUTCInputHourMinute(date: Date): string {
    return [
        date.getUTCHours().toString().padStart(2, "0"),
        date.getUTCMinutes().toString().padStart(2, "0"),
    ].join(":");
}
export function toInputTime(date: Date): string {
    return [
        date.getHours().toString().padStart(2, "0"),
        date.getMinutes().toString().padStart(2, "0"),
        date.getSeconds().toString().padStart(2, "0"),
    ].join(":");
}

export function toInputDateTime(date: Date): string {
    return `${toInputDate(date)}T${toInputTime(date)}`;
}

export function toInputDateHourMinute(date: Date): string {
    return `${toInputDate(date)}T${toInputHourMinute(date)}`;
}

export function getTimeZoneOffset(timeZone: string, date: Date = new Date()) {
    // Use Japan for the standard year/month/day locale
    const [year, month, day, hour, minute, second] = date.toLocaleString("ja", {
        timeZone,
    }).split(/[/\s:]/).map(value => parseInt(value, 10));
    const timeInTimezone = Date.UTC(year, month - 1, day, hour, minute, second);
    const currentTimezone = new Date(date).setMilliseconds(0);
    return (timeInTimezone - currentTimezone) / MINUTE;
}

export function getDateInTimeZone(localDate: Date, fromTimezone: string, timeZone: string) {
    const fromOffset = getTimeZoneOffset(fromTimezone, localDate);
    const toOffset = getTimeZoneOffset(timeZone, localDate);
    return new Date(localDate.getTime() + (fromOffset - toOffset) * MINUTE);
}

export function calculateDateOffset(offset: number, dayNumber: number, currentDate: Date, hours = 0, minutes = 0, seconds = 0) {
    let nextDay = dayNumber - currentDate.getDay();
    if (nextDay <= 0) {
        nextDay += offset;
    }

    return new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + nextDay, hours, minutes, seconds);
}

export const Time = {
    SECOND,
    MINUTE,
    HOUR,
    DAY,
    WEEK,
    MONTH_30,
    MONTH_31,
    YEAR,

    getDateInTimeZone,
};

export function validateMinimumDateAllowed(minDate: Date, localDate: Date) {
    if (minDate.getTime() > localDate.getTime()) {
        return new Date(minDate.getTime() + Time.MINUTE);
    }

    return localDate;
}

export function parseFilterDates(filter: DashboardSettingsFiltersDateInput) {
    let startDate = tryParseDate(filter.startDate)?.getTime();
    let endDate = tryParseDate(filter?.endDate)?.getTime();

    const now = new Date();
    if (filter.preset != null) {
        if (filter.preset === DatePresets.Today) {
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
            endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
        } else if (filter.preset === DatePresets.CurrentWeek) {
            startDate = getStartOfWeek(now).getTime();
            endDate = startDate + Time.WEEK;
        } else if (filter.preset === DatePresets.ThisMonth) {
            startDate = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime() - 1;
        } else if (filter.preset === DatePresets.ThisYear) {
            startDate = new Date(now.getFullYear(), 0, 1).getTime();
            endDate = new Date(now.getFullYear() + 1, 0, 1).getTime() - 1;
        } else if (filter.preset === DatePresets.Last_30Days) {
            startDate = now.getTime() - Time.MONTH_30;
            endDate = Date.now();
        } else if (filter.preset === DatePresets.Last_7Days) {
            startDate = now.getTime() - (Time.DAY * 7);
            endDate = Date.now();
        }
    } else {
        const startDateOffset = filter?.startDateOffset ?? 0;
        if (startDateOffset) {
            startDate = Date.now() - startDateOffset;
        }

        const endDateOffset = filter?.endDateOffset ?? 0;
        if (endDateOffset) {
            endDate = Date.now() - endDateOffset;
        }
    }

    return { startDate, endDate };
}

export function isDateInRange(date: Date | undefined | null, range: {startDate?: Date; endDate?: Date}) {
    return date && (!range.startDate || date.getTime() >= range.startDate?.getTime())
        && (!range.endDate || date.getTime() <= range.endDate?.getTime());
}
